import type { Color } from "../../common/color.js";
import type { Rect } from "../../common/geometry.js";
import type { Direction, RenderContext } from "../render/RenderContext.js";
import type { CaptionButton } from "./Theme.js";

/**
 * Small vector marks shared by the themes.
 *
 * Most are assembled from axis-aligned rects, which is all the renderer drew
 * for a long time. Diagonals go through `drawLine`, which strokes a real
 * antialiased capsule — a diagonal built from rects is a visible staircase at
 * the sizes these are used.
 */

/**
 * A checkmark: a short down-right leg and a long up-right leg.
 *
 * Proportions are the familiar ones — the long leg roughly twice the short —
 * laid out in a unit box and scaled, so it stays the same shape in a 13px
 * menu tick and a 20px checkbox.
 */
export function drawCheckmark(rc: RenderContext, r: Rect, ink: Color): void {
  const s = Math.min(r.width, r.height);
  const cx = r.x + r.width / 2;
  const cy = r.y + r.height / 2;
  // Stroke a little heavier as the mark grows, but never hairline-thin.
  const thickness = Math.max(1.25, s * 0.135);
  const at = (u: number, v: number): { x: number; y: number } => ({
    x: cx + u * s,
    y: cy + v * s,
  });
  rc.drawPolyline([at(-0.3, 0.02), at(-0.09, 0.24), at(0.31, -0.26)], thickness, ink);
}

/** An X, centered in `r`, `size` across. */
export function drawCross(rc: RenderContext, r: Rect, ink: Color, size = 10): void {
  const cx = r.x + r.width / 2;
  const cy = r.y + r.height / 2;
  const half = size / 2;
  const thickness = Math.max(1, Math.round(size / 9));
  rc.drawLine({ x: cx - half, y: cy - half }, { x: cx + half, y: cy + half }, thickness, ink);
  rc.drawLine({ x: cx + half, y: cy - half }, { x: cx - half, y: cy + half }, thickness, ink);
}

/**
 * Minimize / maximize / close, in the flat style every non-Classic theme uses.
 * `restoreBackfill` paints the notch behind the restore glyph's front square.
 */
export function drawCaptionGlyph(
  rc: RenderContext,
  r: Rect,
  button: CaptionButton,
  ink: Color,
  maximized: boolean,
  restoreBackfill: Color,
): void {
  const cx = Math.round(r.x + r.width / 2);
  const cy = Math.round(r.y + r.height / 2);
  const size = 10;
  const half = size / 2;

  switch (button) {
    case "minimize":
      rc.hLine(cx - half, cy, size, ink);
      break;
    case "maximize":
      if (maximized) {
        // Two offset outlines: the "restore down" mark.
        rc.strokeRect({ x: cx - half + 2, y: cy - half, width: size - 2, height: size - 2 }, ink);
        rc.fillRect({ x: cx - half, y: cy - half + 2, width: size - 2, height: size - 2 }, ink);
        rc.fillRect(
          { x: cx - half + 1, y: cy - half + 3, width: size - 4, height: size - 4 },
          restoreBackfill,
        );
      } else {
        rc.strokeRect({ x: cx - half, y: cy - half, width: size, height: size }, ink);
      }
      break;
    case "close":
      drawCross(rc, r, ink, size);
      break;
  }
}

/**
 * A short right-angle tick at each corner of `r` — the bracket motif that
 * makes a plain rectangle read as a targeting reticle or a HUD panel.
 */
export function drawCornerTicks(
  rc: RenderContext,
  r: Rect,
  ink: Color,
  length = 10,
  thickness = 1,
  inset = 0,
): void {
  const x0 = r.x + inset;
  const y0 = r.y + inset;
  const x1 = r.x + r.width - inset;
  const y1 = r.y + r.height - inset;
  const len = Math.min(length, Math.max(0, (x1 - x0) / 2), Math.max(0, (y1 - y0) / 2));
  if (len <= 0) return;

  // Top-left, top-right, bottom-left, bottom-right.
  rc.fillRect({ x: x0, y: y0, width: len, height: thickness }, ink);
  rc.fillRect({ x: x0, y: y0, width: thickness, height: len }, ink);
  rc.fillRect({ x: x1 - len, y: y0, width: len, height: thickness }, ink);
  rc.fillRect({ x: x1 - thickness, y: y0, width: thickness, height: len }, ink);
  rc.fillRect({ x: x0, y: y1 - thickness, width: len, height: thickness }, ink);
  rc.fillRect({ x: x0, y: y1 - len, width: thickness, height: len }, ink);
  rc.fillRect({ x: x1 - len, y: y1 - thickness, width: len, height: thickness }, ink);
  rc.fillRect({ x: x1 - thickness, y: y1 - len, width: thickness, height: len }, ink);
}

/**
 * A cheap outer glow: a few concentric rounded rects at falling opacity.
 * Real blur would need a second render pass; at these radii the banding is
 * invisible and it costs `steps` quads.
 */
export function drawGlow(
  rc: RenderContext,
  r: Rect,
  color: Color,
  radius: number,
  spread = 4,
  steps = 3,
): void {
  for (let i = steps; i >= 1; i--) {
    const grow = (spread * i) / steps;
    rc.fillRect(
      { x: r.x - grow, y: r.y - grow, width: r.width + grow * 2, height: r.height + grow * 2 },
      { ...color, a: color.a * (0.16 / i) },
      radius + grow,
    );
  }
}

/** A filled triangle, used for scroll steppers and disclosure marks. */
export function drawTriangle(
  rc: RenderContext,
  box: Rect,
  direction: Direction,
  ink: Color,
  size = 4,
): void {
  // `size` has always meant the height; the base is a shade under twice it,
  // which is the proportion the old stacked-rect version happened to have.
  const height = Math.max(2, size);
  const width = height * 2 - 1;
  const horizontal = direction === "left" || direction === "right";
  const w = horizontal ? height : width;
  const h = horizontal ? width : height;
  rc.fillTriangle(
    {
      x: Math.round(box.x + (box.width - w) / 2),
      y: Math.round(box.y + (box.height - h) / 2),
      width: w,
      height: h,
    },
    direction,
    ink,
  );
}

/**
 * The three stacked bars of a hamburger button.
 *
 * Sized from the box rather than fixed, so it reads the same next to a Win95
 * bevel and a Fluent pill. Bar thickness is rounded to whole pixels because a
 * half-pixel rule at this size turns gray.
 */
export function drawHamburger(rc: RenderContext, box: Rect, ink: Color, bars = 3): void {
  const width = Math.max(2, Math.round(Math.min(box.width, box.height) * 0.62));
  const thickness = Math.max(1, Math.round(width / 9));
  const gap = Math.max(thickness + 1, Math.round(width / 4));
  const span = bars * thickness + (bars - 1) * gap;
  const x = Math.round(box.x + (box.width - width) / 2);
  const top = Math.round(box.y + (box.height - span) / 2);
  for (let i = 0; i < bars; i++) {
    rc.fillRect({ x, y: top + i * (thickness + gap), width, height: thickness }, ink);
  }
}
